import { useState, useEffect, useContext } from 'react';
import type { Context } from 'react';

import { ApiRoute } from 'config';
import { get } from 'utils/network';

import FetchState from 'models/FetchState';

interface IContext {
  dispatch: (action: unknown) => void;
  fetchState: FetchState;
}

function createUseContextHook<TContext extends IContext>(
  context: Context<TContext>,
  route: ApiRoute,
): typeof useGetResource {
  type UseGetResource = TContext & { reload: () => void };
  let requestsInProgress = [];

  const useGetResource = (): UseGetResource => {
    const contextValue = useContext<TContext>(context);
    const [reloadTrigger, setReloadTrigger] = useState(false);

    if (contextValue === null) {
      throw new Error(
        'Contexts must be used within their correspondent providers',
      );
    }

    const { dispatch, fetchState } = contextValue;

    const reload = () => {
      dispatch({ type: 'setFetchState', data: FetchState.RELOADING });
      setReloadTrigger(true);
    };

    useEffect(() => {
      (async () => {
        if (
          (!requestsInProgress.length && fetchState === FetchState.IN_PROGRESS)
          || reloadTrigger
        ) {
          setReloadTrigger(false);

          const request = get(route);
          // @ts-expect-error TODO: fix null safety
          requestsInProgress.push(request);

          const response = await request;
          // @ts-expect-error TODO: fix null safety
          if (requestsInProgress.indexOf(request) === requestsInProgress.length - 1) {
            requestsInProgress = [];

            if (response.ok) {
              dispatch({ type: 'reload', data: response.parsedBody });
              dispatch({ type: 'setFetchState', data: FetchState.SUCCESS });
            } else {
              dispatch({ type: 'setFetchState', data: FetchState.ERROR });
            }
          }
        }
      })();
    }, [dispatch, fetchState, reloadTrigger]);

    return { ...contextValue, reload };
  };

  return useGetResource;
}

export default createUseContextHook;
